import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

import { Autocomplete } from '@tmap/mmm-style-guide/src/Autocomplete';
import useDebounced from '@tmap/mmm-style-guide/src/useDebounced';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { TextField } from '@tmap/mmm-style-guide/src/TextField';

import { getGeonameAutocomplete } from '../actions/geonames';

export default function LocationAutocomplete(props) {
  const {
    value,
    setValue,
    useThunk,
    color = 'primary',
    label = 'Moving From',
    ...autocompleteProps
  } = props;
  const [inputValue, setInputValue] = useState('');

  const [state, getAutocomplete] = useThunk(getGeonameAutocomplete);
  const autocomplete = useMemo(() => state.data?.autocomplete || [], [state.data?.autocomplete]);

  const fetchAutocomplete = useDebounced(getAutocomplete, 400);

  useEffect(() => {
    if (inputValue) fetchAutocomplete(inputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const filledInput = Boolean(inputValue);
  const autocompleteFetched = filledInput ? Boolean(autocomplete) : false;

  const isOptionEqualToValue = (option, currentValue) => (
    option.geoname_id === currentValue.geoname_id
  );

  // the component will complain if the current value is not present in options
  // add it to the end if it does not exist already
  const optionsPlusValue = useMemo(() => {
    const options = filledInput ? autocomplete : [];
    if (value !== null && !options.find((option) => isOptionEqualToValue(option, value))) {
      return [...options, value];
    }
    return options;
  }, [autocomplete, filledInput, value]);

  const renderInput = useCallback((inputProps) => (
    <TextField
      {...inputProps}
      color={color}
      label={label}
    />
  ), [color, label]);

  const renderOption = useCallback((optionProps, option) => {
    const county = option?.coliData?.county;
    return (
      <li {...optionProps} key={option.geoname_id}>
        <Grid container alignItems='center'>
          <Grid item xs>
            {option.name}
            <Typography variant='body2' color='textSecondary'>
              {county ? `${county}, ` : ''}{option.state}
            </Typography>
          </Grid>
        </Grid>
      </li>
    );
  }, []);

  return (
    <Autocomplete
      color={color}
      fullWidth
      noOptionsText={autocompleteFetched ? 'No matching locations' : 'Type to search'}
      getOptionLabel={(option) => `${option.name}, ${option.state_abbreviation}`}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(opts, muiState) => opts.filter((i) => {
        const itemName = [i?.name, i?.state, i?.state_abbreviation, ...(i?.alternatenames || [])].join(' ').toLowerCase();
        const inputTokens = muiState.inputValue.toLowerCase().split(/[\s,]+/);
        return inputTokens.some((token) => itemName.indexOf(token) >= 0);
      })}
      options={optionsPlusValue}
      autoComplete
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={renderInput}
      renderOption={renderOption}
      {...autocompleteProps}
    />
  );
}
